// src/pages/Login.js
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { setAuth } from '../utils/auth';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/NotificationContainer';
import { analyticsEvents } from '../utils/analyticsEvents';

const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { notifications, showSuccess, showError, removeNotification } = useNotification();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/login', { email, password });
      setAuth(response.data.token, response.data.user);
      analyticsEvents.loginSuccess(response.data.user?.role);
      
      // Redirect based on user role
      const user = response.data.user;
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'journalist') {
        navigate('/journalist');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      // Extract error message - handle both string and object formats
      let errorMsg = 'Login failed';
      if (err.response?.data?.error) {
        if (typeof err.response.data.error === 'string') {
          errorMsg = err.response.data.error;
        } else if (err.response.data.error.message) {
          errorMsg = err.response.data.error.message;
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      // Check if it's an email verification error - show helpful message
      const isVerificationError = errorMsg.toLowerCase().includes('verify') || 
                                  errorMsg.toLowerCase().includes('verification');
      
      if (isVerificationError) {
        // Show error but also show success notification
        setError(errorMsg);
        showSuccess('A new verification email has been sent! Please check your inbox and click the verification link to activate your account.');
      } else {
        setError(errorMsg);
        showError(errorMsg);
      }
      analyticsEvents.loginFailure(isVerificationError ? 'email_verification_required' : 'invalid_credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-shayek-light-50 to-shayek-light-100 dark:from-shayek-dark dark:to-shayek-dark-50 px-4 transition-colors">
      <NotificationContainer 
        notifications={notifications} 
        onRemove={removeNotification} 
      />
      <div className="max-w-md w-full bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek-lg p-8 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
        <h2 className="text-3xl font-bold text-center text-shayek-light-900 dark:text-shayek-dark-900 mb-8">
          AI Moderation Platform
        </h2>
        <h3 className="text-xl font-semibold text-center text-shayek-light-600 dark:text-shayek-dark-600 mb-6">
          Sign In
        </h3>

        {error && (
          <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/20 border-2 border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900"
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full px-4 py-2 pr-10 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-shayek-light-600 dark:text-shayek-dark-600 hover:text-shayek-light-900 dark:hover:text-shayek-dark-900"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m13.42 13.42l-3.29-3.29M3 3l13.42 13.42" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-shayek-primary dark:bg-shayek-primary-600 text-white py-2 px-4 rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-shayek"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p className="mt-4 text-center text-sm text-shayek-light-600 dark:text-shayek-dark-600">
          <Link to="/forgot-password" className="text-shayek-primary hover:text-shayek-primary-600 dark:text-shayek-red-light dark:hover:text-shayek-red-light font-medium transition-colors">
            {t('auth.forgotPassword')}
          </Link>
        </p>
        <p className="mt-2 text-center text-sm text-shayek-light-600 dark:text-shayek-dark-600">
          Don't have an account?{' '}
          <Link to="/register" className="text-shayek-primary hover:text-shayek-primary-600 dark:text-shayek-red-light dark:hover:text-shayek-red-light font-medium transition-colors">
            Sign up
          </Link>
        </p>
        
        {error && (error.toLowerCase().includes('verify') || error.toLowerCase().includes('verification')) && (
          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-400 dark:border-blue-800 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-1">
                  Email Verification Required
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-400 mb-2">
                  A new verification email has been sent to <strong>{email}</strong>. Please check your inbox (and spam folder) and click the verification link to activate your account.
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-500">
                  After verifying, you can log in with your credentials.
                </p>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Login;

